/**
 * Web トラッキング UI（タスク48）。ログイン中のみ表示。メニュー項目 → モーダルで操作する。
 *
 * 状態と送信ループは App の useTracking が持つ（このコンポーネントはモーダルを閉じるとアンマウントされるため）。
 * - 停止中: 共有する時間（15分／1時間／8時間／停止するまで）を選んで開始
 * - 動作中: 残り時間・最終送信時刻・問題があればその理由と、停止ボタン
 * 「地図に表示する」がオフだと送っても地図に出ないので、開いたときに確認して知らせる。
 *
 * トラッキングはブラウザの位置情報が必須（タスク49）。ブロック中・非対応は「開始」を押せないようにし、
 * 「開始」ではまず現在地を1回取って、取れたときだけ始める（始まったように見えて裏で止まる、を防ぐ）。
 */
import { useEffect, useRef, useState } from 'react';
import { getMyProfile } from '../api/client';
import { loadTokens } from './cognito';
import type { UseAuthResult } from './useAuth';
import type { TrackingDuration, UseTrackingResult } from './useTracking';
import type { Lang } from '../i18n/lang';
import { messages } from '../i18n/messages';
import { useMenuModal } from './userMenuContext';
import { useGeolocationPermission } from './useGeolocationPermission';
import './trackingControl.css';

const DURATIONS: TrackingDuration[] = ['15m', '1h', '8h', 'indefinite'];

export interface TrackingControlProps {
  readonly auth: UseAuthResult;
  /** App で一度だけ呼んだ useTracking の結果。 */
  readonly tracking: UseTrackingResult;
  readonly lang: Lang;
}

export function TrackingControl({ auth, tracking, lang }: TrackingControlProps) {
  const { enabled, user, loading } = auth;
  const loggedIn = enabled && !loading && !!user;
  const m = messages(lang);

  const [open, setOpen] = useMenuModal();
  const [duration, setDuration] = useState<TrackingDuration>('1h');
  /** 「地図に表示する」がオフか（開いたときに確認・不明なら null）。 */
  const [hidden, setHidden] = useState<boolean | null>(null);
  /** 残り時間の表示を更新するための現在時刻。 */
  const [now, setNow] = useState(() => Date.now());
  /** 「開始」で現在地を確認中か。 */
  const [starting, setStarting] = useState(false);
  /** 開始前の現在地確認に失敗した理由。 */
  const [startError, setStartError] = useState<'denied' | 'unavailable' | 'timeout' | null>(
    null,
  );
  // 位置情報の許可状態。許可に変わったら前回の失敗表示を消す（押せるようになる）
  const permission = useGeolocationPermission(open, () => setStartError(null));
  // 「現在地を確認中…」の間にモーダルを閉じたら、あとから位置が取れても開始しない
  // （start は App 側の関数なので、閉じてアンマウントされた後でも呼べてしまう）
  const openRef = useRef(open);
  openRef.current = open;
  useEffect(
    () => () => {
      openRef.current = false;
    },
    [],
  );

  // 残り時間の表示を進める（メニュー項目にも出すので、マウント中は常に回す）
  useEffect(() => {
    if (!tracking.active || tracking.endsAt === null) {
      return;
    }
    const timer = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(timer);
  }, [tracking.active, tracking.endsAt]);

  async function openModal(): Promise<void> {
    setNow(Date.now());
    setHidden(null);
    setStarting(false);
    setStartError(null);
    setOpen(true);
    const token = loadTokens()?.id_token;
    if (!token) {
      return;
    }
    try {
      const profile = await getMyProfile(token);
      setHidden(!profile.visible);
    } catch (err) {
      console.error('tracking visibility check failed', err);
    }
  }

  /** まず現在地を1回取り、取れたらその位置を渡して開始する。取れなければ開始せず理由を出す。 */
  function handleStart(): void {
    setStarting(true);
    setStartError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setStarting(false);
        if (!openRef.current) {
          return;
        }
        tracking.start(duration, {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
        setOpen(false);
      },
      (err) => {
        console.warn('tracking start geolocation failed', err.code, err.message);
        setStarting(false);
        setStartError(
          err.code === err.PERMISSION_DENIED
            ? 'denied'
            : err.code === err.TIMEOUT
              ? 'timeout'
              : 'unavailable',
        );
      },
      { enableHighAccuracy: true, timeout: 20_000, maximumAge: 30_000 },
    );
  }

  if (!loggedIn) {
    return null;
  }

  const blocked = permission === 'denied' || permission === 'unsupported';

  const remainingText =
    tracking.endsAt === null
      ? m.tracking.indefinite
      : m.tracking.remaining(Math.max(0, Math.round((tracking.endsAt - now) / 1000)));
  const timeFormat = new Intl.DateTimeFormat(lang === 'ja' ? 'ja-JP' : 'en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const problemText = tracking.problem ? m.tracking.problem[tracking.problem] : null;

  return (
    <>
      <button type="button" className="user-menu__item" onClick={() => void openModal()}>
        {tracking.active ? (
          // 1行に詰めると日本語では途中で折り返すので、残り時間は2行目に小さく出す（タスク48.4）
          <>
            {m.tracking.triggerActive}
            <span className="user-menu__item-sub">{remainingText}</span>
          </>
        ) : (
          m.tracking.trigger
        )}
      </button>

      {open && (
        <div className="tracking-modal__overlay" onClick={() => setOpen(false)}>
          <div
            className="tracking-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={m.tracking.title}
          >
            <h2 className="tracking-modal__title">{m.tracking.title}</h2>
            <p className="tracking-modal__note">{m.tracking.note}</p>

            {hidden && <p className="tracking-modal__warn">{m.tracking.hiddenWarning}</p>}
            {problemText && <p className="tracking-modal__warn">{problemText}</p>}

            {tracking.active ? (
              <>
                <p className="tracking-modal__status">
                  📡 {m.tracking.sharing(remainingText)}
                  <br />
                  <span className="tracking-modal__sub">
                    {tracking.lastSentAt
                      ? m.tracking.lastSent(timeFormat.format(tracking.lastSentAt))
                      : m.tracking.notSentYet}
                  </span>
                </p>
                <button
                  type="button"
                  className="tracking-modal__stop"
                  onClick={() => {
                    tracking.stop();
                    setOpen(false);
                  }}
                >
                  {m.tracking.stop}
                </button>
              </>
            ) : (
              <>
                <fieldset className="tracking-modal__durations">
                  <legend>{m.tracking.durationLabel}</legend>
                  {DURATIONS.map((d) => (
                    <label key={d}>
                      <input
                        type="radio"
                        name="tracking-duration"
                        checked={duration === d}
                        onChange={() => setDuration(d)}
                      />
                      {m.tracking.durations[d]}
                    </label>
                  ))}
                </fieldset>
                {permission === 'denied' && (
                  <p className="tracking-modal__warn">{m.tracking.needPermission}</p>
                )}
                {permission === 'unsupported' && (
                  <p className="tracking-modal__warn">{m.tracking.unsupported}</p>
                )}
                {startError && !blocked && (
                  <p className="tracking-modal__warn">{m.tracking.startFailed[startError]}</p>
                )}
                <button
                  type="button"
                  className="tracking-modal__start"
                  onClick={handleStart}
                  disabled={starting || blocked}
                >
                  {starting ? m.tracking.checking : m.tracking.start}
                </button>
              </>
            )}

            <button
              type="button"
              className="tracking-modal__close"
              onClick={() => setOpen(false)}
            >
              {m.common.close}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
